/**
 * The backend (dk_common `TelephoneNumber`) only accepts compact E.164:
 *   +<country><3 digits><3 digits><4-6 digits>, separators optional but rare.
 * This is the single source of truth the frontend must satisfy — keep it in
 * lockstep with `dk_common/utils/annotations.py::validate_phone_number`.
 */
export const BACKEND_PHONE_REGEX = /^[+][(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;

/** Belgium — De Kastenman's primary market; assumed for nationally-formatted input. */
const DEFAULT_COUNTRY_CODE = '32';

/**
 * Convert a loosely-typed phone number into the compact, +-prefixed E.164 form
 * the backend expects. Belgians naturally type "0470 12 34 56" or
 * "+32 470 12 34 56"; both pass the (deliberately forgiving) client-side
 * validator but are rejected by the stricter backend regex, which silently 422s
 * the create-user/update-user call. Normalising here closes that gap so what the
 * user can type is always what the backend can accept.
 *
 * Returns `null` for blank input; returns the trimmed original untouched when it
 * holds no digits at all, so validation (not this helper) reports the error.
 */
export function normalizePhoneNumber(value: string | null | undefined): string | null {
	if (value === null || value === undefined) {
		return null;
	}

	const trimmed = value.toString().trim();
	if (!trimmed) {
		return null;
	}

	const digits = trimmed.replace(/\D/g, '');
	if (!digits) {
		return trimmed;
	}

	if (trimmed.startsWith('+')) {
		return `+${digits}`;
	}
	if (digits.startsWith('00')) {
		// 0032 470... -> +32 470...
		return `+${digits.slice(2)}`;
	}
	if (digits.startsWith('0')) {
		// National trunk prefix: 0470... -> +32470...
		return `+${DEFAULT_COUNTRY_CODE}${digits.slice(1)}`;
	}
	if (digits.startsWith(DEFAULT_COUNTRY_CODE)) {
		// Country code typed without the +: 32470... -> +32470...
		return `+${digits}`;
	}
	return `+${DEFAULT_COUNTRY_CODE}${digits}`;
}
